import { useIntl } from 'react-intl';
import {
  Form,
  Button,
  FreeForm,
  FormItem,
  FormPage,
  Select,
  DatePicker,
} from 'iglooform';
import { useRequest } from 'ahooks';
import classnames from 'classnames';
import { getSession } from '@/utils/common';
import { history, useLocation } from 'umi';
import { CreateQuote, QuotationConfirm } from '../service';
import useMobile from '@/hooks/useMobile';
import useFormItemReaction from './hooks/useFormItemReaction';
import * as SESSION_KEYS from '@/configs/session-storage-keys';

import './index.less';

interface Props {
  nextStep: string;
  productType: string;
  needCreateQuote?: boolean;
  changeActiveStep: any;
  isBuyNowFlow?: boolean;
}

const TravelInfo = (props: Props) => {
  const {
    changeActiveStep,
    nextStep,
    needCreateQuote,
    productType,
    isBuyNowFlow = false,
  } = props;
  const isMobile = useMobile();
  const location: any = useLocation();
  const planPicked = getSession(SESSION_KEYS.PICKED_PLAN_INFO);
  const initFirstStepInfo = getSession(SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE);

  const { formatMessage } = useIntl();
  const form = Form.useForm();

  const { run: quotationConfirm } = useRequest(QuotationConfirm, {
    manual: true,
    onSuccess: () => {
      const quoteInfo = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.QUOTE_INFO) ?? '{}',
      );
      const historyJumpFunc = isBuyNowFlow ? history.push : history.replace;
      historyJumpFunc({
        pathname: `/home/${productType}/application`,
        query: {
          productKey: planPicked?.productKey,
          planKey: planPicked?.planKey,
          quotationId: quoteInfo?.id,
          goBackUrl: location.pathname + '?' + location.search,
          isBuyNowFlow: String(isBuyNowFlow),
        },
      });

      //event tracking: start_application
      window.gtag('event', 'start_application', {
        page: 'direct purchase',
        product_key: planPicked?.productKey,
        plan_key: planPicked?.planKey,
        quote_id: quoteInfo?.id,
        user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
          ?.id,
      });
    },
  });

  const { continueButtonDisalbed, onChangeFormItem, disabledDate } =
    useFormItemReaction(initFirstStepInfo);
  const { run: createQuote } = useRequest(CreateQuote, {
    manual: true,
    onSuccess: (res) => {
      sessionStorage.setItem(SESSION_KEYS.QUOTE_INFO, JSON.stringify(res));
      if (nextStep === 'application') {
        quotationConfirm(
          productType,
          planPicked?.productKey,
          planPicked?.planKey,
          res?.id,
        );
      } else {
        changeActiveStep(nextStep);
      }
    },
  });

  return (
    <div className={classnames(['vehicleForm', { 'is-mobile': isMobile }])}>
      <FreeForm
        className="formContainer"
        form={form}
        showSubmitButton={false}
        initialValues={{
          travelInfo: initFirstStepInfo,
        }}
      >
        <FormPage name="travelInfo">
          <FormItem
            name="destination"
            label={formatMessage({
              id: 'Destination',
            })}
            fullRow
          >
            <Select
              className="vehicleFormSelectVehicle"
              style={!isMobile ? { width: '48.9%' } : {}}
              placeholder={formatMessage({ id: 'Make A Selection' })}
              onChange={(value) => onChangeFormItem('destination', value)}
            >
              {[
                { label: formatMessage({ id: 'SEA' }), value: 'SEA' },
                { label: formatMessage({ id: 'Asia' }), value: 'Asia' },
                {
                  label: formatMessage({ id: 'Worldwide' }),
                  value: 'Worldwide',
                },
              ].map((e) => (
                <Select.Option key={e.value} value={e.value}>
                  {e.label}
                </Select.Option>
              ))}
            </Select>
          </FormItem>

          <FormItem
            name="travel_start_date"
            label={formatMessage({
              id: 'Travel Start Date',
            })}
          >
            <DatePicker
              format={'YYYY-MM-DD'}
              placeholder={' '}
              onChange={(value) => onChangeFormItem('travel_start_date', value)}
              disabledDate={disabledDate.start}
            />
          </FormItem>

          <FormItem
            name="travel_end_date"
            label={formatMessage({
              id: 'Travel End Date',
            })}
          >
            <DatePicker
              format={'YYYY-MM-DD'}
              placeholder={' '}
              onChange={(value) => onChangeFormItem('travel_end_date', value)}
              disabledDate={disabledDate.end}
            />
          </FormItem>
        </FormPage>
      </FreeForm>

      <Button
        className={'continueButton'}
        type="primary"
        disabled={continueButtonDisalbed}
        onClick={() => {
          form.validateFields().then((value) => {
            sessionStorage.setItem(
              SESSION_KEYS.FIRST_STEP_INFO_FAST_QUOTE,
              JSON.stringify(value.travelInfo),
            );
            if (needCreateQuote) {
              const travelInfoJSON = value.travelInfo;
              if (location?.query?.button === 'buyNow') {
                travelInfoJSON.process_for_buy_now = true;
              }
              createQuote(
                productType,
                planPicked?.productKey,
                planPicked?.planKey,
                travelInfoJSON,
              );
            } else {
              changeActiveStep(nextStep);
            }
          });
        }}
      >
        {formatMessage({ id: 'Next' })}
      </Button>
    </div>
  );
};

export default TravelInfo;
